import { hcWithType } from '@ocarina/api/hc'
import { ClientResponse } from 'hono/client'
import type { StatusCode } from 'hono/utils/http-status'

import { getBearerToken } from '@/clients/auth'

export const apiClient = hcWithType(import.meta.env.VITE_API_BASE, {
  // @ts-expect-error fix type
  fetch: async (input, init) => {
    const headers = new Headers(init?.headers)
    const token = getBearerToken()

    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }

    return fetch(input, {
      ...init,
      headers,
    })
  },
}).api

// Define a custom error type for fetch operations
export class HcFetchError<T = unknown> extends Error {
  public statusCode: number
  public data: T

  constructor(statusCode: number, responseBody: T) {
    super(`Request failed with status ${statusCode}`)
    this.name = 'HcFetchError' // Set the error name
    this.statusCode = statusCode
    this.data = responseBody

    // Maintain proper stack trace for where our error was thrown (only available on V8 engines)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, HcFetchError)
    }
  }
}

type SuccessStatusCode =
  | 200
  | 201
  | 202
  | 203
  | 204
  | 205
  | 206
  | 207
  | 208
  | 226

type ExtractOkBody<R> =
  R extends ClientResponse<infer T, infer S, string>
    ? S extends SuccessStatusCode
      ? T
      : never
    : never

export async function formatClientResponse<
  R extends ClientResponse<unknown, StatusCode, string>,
>(r: R): Promise<ExtractOkBody<R>> {
  if (r.ok) {
    return (await r.json()) as ExtractOkBody<R>
  }

  throw new HcFetchError(r.status, await r.json())
}
